import * as React from 'react';

import { CardStatusTag } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

import _t from 'i18n!nls/product-card';

const OUTCOMES_BASED_SLUG_LIST = [
  'imba',
  'bachelor-of-science-computer-science-london',
  'master-of-data-science-university-of-pittsburgh',
  'bachelor-of-science-psychology-jgu',
  'mba-business-analytics-jgu',
  'msc-management-huddersfield',
  'me-engineering-management-boulder',
  'bachelor-of-science-data-science-ai-iitguwahati',
  'ms-management-illinois',
  'bachelor-of-science-computer-science-bits',
  'bsc-data-science-huddersfield',
  'ba-liberal-studies-georgetown',
  'ms-artificial-intelligence-boulder',
  'ma-international-relations-security-strategy-jgu',
  'msc-cyber-security-london',
  'msc-computer-science-heriot-watt',
  'unt-online-bachelor-completion',
  'masters-in-computer-data-science',
  'ms-computer-science-boulder',
  'msee-boulder',
  'mas-data-science-illinois-tech',
  'emba-iitr',
  'bachelor-of-science-general-business-unt',
  'ms-data-analytics-engineering-northeastern',
  'bsc-business-administration-london',
  'imsa',
  'master-of-science-data-science-boulder',
  'meng-computer-engineering-dartmouth',
  'mba-illinois-tech',
  'mas-engineering-berkeley',
  'ma-public-policy-jgu',
  'bsc-marketing-london',
  'ms-engineering-management-northeastern',
  'bach-information-technology-illinois-tech',
  'omie',
  'mas-information-technology-illinois-tech',
  'master-computer-science-clemson',
  'ms-data-science-ball-state',
  'ms-computer-science-ball-state',
  'ifp-computer-science-london',
  'msc-data-science-ul',
  'google-advanced-data-analytics',
  'sap-business-analyst',
  'intuit-bookkeeping',
  'microsoft-sql-server',
  'microsoft-python-developer',
  'meta-front-end-developer',
  'meta-full-stack-developer',
  'ibm-business-analyst-professional-certificate',
  'aapc-medical-biller',
  'aws-cloud-solutions-architect',
  'sap-technology-consultant',
  'meta-data-analyst',
  'ibm-product-manager',
  'microsoft-full-stack-developer',
  'meta-back-end-developer',
  'johns-hopkins-medical-office-manager',
  'ec-council-information-security-analyst',
  'keller-williams-real-estate-agent',
  'nasm-personal-fitness-trainer',
  'hrci-human-resource-associate',
];

const GENERIC_SOCIAL_PROOF_SLUG_LIST = [
  'financial-markets-global',
  'foundations-of-digital-marketing-and-e-commerce',
  'google-it-automation',
  'foundations-data',
  'learn-korean',
  'brand',
  'generative-ai-with-llms',
  'technical-support-fundamentals',
  'sciwrite',
  'retrieval-augmented-generation-rag',
  'microsoft-data-visualization',
  'adp-payroll-specialist',
  'bi-analyst',
  'google-data-analysis-with-python',
  'ibm-project-manager',
  'ibm-it-project-manager',
  'ibm-full-stack-javascript-developer',
  'google-agile-essentials',
  'positivepsychology',
  'salesforce-sales-operations',
  'negotiation-skills',
  'tableau-business-intelligence-analyst',
  'python-3-programming',
  'learn-sql-basics-data-science',
  'jhu-data-science',
  'programming-python-java',
  'google-people-management-essentials',
  'data-science-fundamentals-python-sql',
  'ibm-data-analyst-r-excel',
  'software-engineering-fundamentals',
  'sales-development-representative',
  'wharton-success',
  'pearson-pmp-certification',
  'business-financial-management',
  'ibm-isc2-cybersecurity-specialist',
  'decentralized-finance-duke',
  'data-science-foundations',
  'ibm-data-manager',
  'indigenous-canada',
  'statistical-inference-for-data-science-applications',
];

const AI_DIFFERENTIATOR_SLUG_LIST = [
  'google-data-analytics',
  'google-project-management',
  'google-digital-marketing-ecommerce',
  'google-cybersecurity',
  'google-it-support',
  'microsoft-power-bi-data-analyst',
  'google-ux-design',
  'ibm-data-science',
  'ibm-data-analyst',
  'facebook-social-media-marketing',
  'ibm-full-stack-cloud-developer',
  'microsoft-cybersecurity-analyst',
  'ibm-ai-product-manager',
  'microsoft-project-management',
  'microsoft-business-analyst',
  'ibm-cybersecurity-analyst',
  'data-analytics',
  'adobe-graphic-designer',
  'microsoft-it-support-specialist',
  'ibm-data-engineer',
  'adobe-content-creator',
  'amazon-junior-software-developer',
  'java-developer',
  'facebook-marketing-analytics',
  'adobe-marketing-specialist',
  'ux-design',
  'ibm-digital-marketing-and-growth-hacking',
  'ibm-backend-development',
  'google-cloud-cybersecurity-certificate',
  'google-cloud-data-analytics-certificate',
  'ibm-systems-analyst',
];

const AI_SOCIAL_PROOF_SLUG_LIST = [
  'ai-essentials-google',
  'machine-learning-introduction',
  'generative-ai-for-everyone',
  'introduction-to-generative-ai',
  'fundamentals-of-machine-learning-and-artificial-intelligence',
  'ibm-generative-ai-engineering',
  'ai-engineer',
  'applied-artifical-intelligence-ibm-watson-ai',
  'chatgpt-free-ai-tools-to-excel',
  'microsoft-ai-product-manager',
  'generative-ai-for-leaders',
  'ai-foundations-for-everyone',
  'ibm-rag-and-agentic-ai',
  'ai-healthcare',
  'ai-for-business-wharton',
  'microsoft-ai-and-ml-engineering',
  'mathematics-for-machine-learning-and-data-science',
  'machine-learning-scikit-learn-pytorch-hugging-face',
  'machine-learning',
  'ai-agents',
  'ibm-machine-learning',
  'building-ai-agents-and-agentic-workflows',
  'cryptocurrency',
  'mathematics-machine-learning',
  'adp-airs-entry-level-recruiter',
  'data-engineering',
  'aws-generative-ai-developers',
  'ai-agents-for-leaders',
  'html-css-javascript-for-web-developers',
  'ai-agents-python',
  'generative-ai-for-software-development',
  'vibe-coding',
  'google-cloud-digital-leader-training',
  'generative-ai-for-growth-marketing',
  'generative-ai-strategic-leader',
];

const FRESHNESS_SIGNAL_SLUG_LIST = [
  'google-ai',
  'microsoft-ai-agents',
  'organizational-strategy-generative-ai',
  'strengthen-your-business-savvy',
  'canva-essentials',
  'boost-your-leadership-impact',
  'ibm-systems-and-solutions-architect',
  'generative-ai-for-digital-marketing',
  'sales-excellence',
  'microsoft-introduction-to-c-plus-plus-programming',
  'real-world-ai-for-everyone',
  'microsoft-excel-mastery-from-basics-to-pro-skills',
  'microsoft-generative-ai-engineering',
  'seo-mastery-from-fundamentals-to-genai-and-geo-strategies',
  'microsoft-powerbi-developer-and-architect',
  'airbus-supply-chain-specialist',
  'filmmaking',
  'ai-for-finance',
  'lead-for-results-from-planning-to-execution',
  'digital-marketing-certification-course',
  'ai-marketing-mastery',
  'authentic-communication-with-confidence-and-impact',
  'python-developer-basics-to-case-studies',
  'psychology-of-disorders-related-to-trauma-anxiety-and-stress',
  'pytorch-for-deep-learning',
  'mastering-claude-ai-prompting-apis-rag-and-mcp',
  'build-high-performing-teams',
  'microsoft-365-copilot',
  'estimating-and-bidding-in-construction',
  'machine-learning-specialization',
];

export const getOutcomesBasedTag = () => {
  const variant = GrowthDiscoveryEpicClient.get('statusTagCopyOutcomesBased');
  if (variant === 'A') {
    return _t('Hands-On Projects');
  }
  if (variant === 'B') {
    return _t('Job Ready');
  }
  return undefined;
};

export const shouldShowOutcomesBasedTag = (slug: string) => {
  if (
    OUTCOMES_BASED_SLUG_LIST.includes(slug) &&
    GrowthDiscoveryEpicClient.get('statusTagCopyOutcomesBased') !== 'control'
  ) {
    return true;
  }
  return false;
};

export const getAiDifferentiatorTag = () => {
  const variant = GrowthDiscoveryEpicClient.get('statusTagCopyAiDifferentiator');
  if (variant === 'A') {
    return <CardStatusTag icon={<AIGenerateIcon size="small" />}>{_t('AI Skills')}</CardStatusTag>;
  }
  if (variant === 'B') {
    return _t('Job Skills');
  }
  return undefined;
};

export const shouldShowAiDifferentiatorTag = (slug: string) => {
  if (
    AI_DIFFERENTIATOR_SLUG_LIST.includes(slug) &&
    GrowthDiscoveryEpicClient.get('statusTagCopyAiDifferentiator') !== 'control'
  ) {
    return true;
  }
  return false;
};

export const getAiSocialProofTag = () => {
  const variant = GrowthDiscoveryEpicClient.get('statusTagCopyAiSocialProof');
  if (variant === 'A') {
    return _t('Top AI Program');
  }
  if (variant === 'B') {
    return _t('Trending in AI');
  }
  return undefined;
};

export const shouldShowAiSocialProofTag = (slug: string) => {
  if (
    AI_SOCIAL_PROOF_SLUG_LIST.includes(slug) &&
    GrowthDiscoveryEpicClient.get('statusTagCopyAiSocialProof') !== 'control'
  ) {
    return true;
  }
  return false;
};

export const getGenericSocialProofTag = () => {
  const variant = GrowthDiscoveryEpicClient.get('statusTagCopyGenericSocialProof');
  if (variant === 'A') {
    // eslint-disable-next-line no-restricted-syntax
    return _t('Popular This Week');
  }
  if (variant === 'B') {
    return _t('Bestseller');
  }
  return undefined;
};
export const shouldShowGenericSocialProofTag = (slug: string) => {
  if (
    GENERIC_SOCIAL_PROOF_SLUG_LIST.includes(slug) &&
    GrowthDiscoveryEpicClient.get('statusTagCopyGenericSocialProof') !== 'control'
  ) {
    return true;
  }
  return false;
};

export const getFreshnessSignalTag = () => {
  const variant = GrowthDiscoveryEpicClient.get('statusTagCopyFreshnessSignal');
  if (variant === 'A') {
    return _t('New');
  }
  if (variant === 'B') {
    return _t('Hot New Release');
  }
  return undefined;
};

export const shouldShowFreshnessSignalTag = (slug: string) => {
  if (
    FRESHNESS_SIGNAL_SLUG_LIST.includes(slug) &&
    GrowthDiscoveryEpicClient.get('statusTagCopyFreshnessSignal') !== 'control'
  ) {
    return true;
  }
  return false;
};
